import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import Swal from "sweetalert2";
import { confirmAlert, errorAlert, successAlert } from "../../utils/alert";

const initialForm = {
  name: "",
  email: "",
  password: "",
  role: "district",
  district: "",
  district_id: "",
  photo: null,
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value || "").toLowerCase() === "true" || String(value || "") === "1";
};

const DistrictRegistration = () => {
  const [form, setForm] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [districtLogoFiles, setDistrictLogoFiles] = useState({});
  const [districtLogoUploading, setDistrictLogoUploading] = useState({});

  const fetchDistrictOptions = async () => {
    try {
      const rows = await API.fetchDistrictOptions();
      setDistrictOptions(Array.isArray(rows) ? rows : []);
    } catch {
      setDistrictOptions([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.fetchAdminUsers();
      setUsers(Array.isArray(res) ? res : []);
    } catch (err) {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDistrictOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "photo") {
      setForm((prev) => ({ ...prev, photo: files?.[0] || null }));
      return;
    }
    if (name === "role" && value !== "district") {
      setForm((prev) => ({ ...prev, role: value, district: "", district_id: "" }));
      return;
    }
    if (name === "district_id") {
      const selected = districtOptions.find((d) => {
        const optionValue = d.id ? `id:${d.id}` : `name:${d.name}`;
        return optionValue === value;
      });
      setForm((prev) => ({
        ...prev,
        district_id: value,
        district: selected?.name || "",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("role", form.role);
      fd.append("district", form.district || "");
      if (String(form.district_id || "").startsWith("id:")) {
        fd.append("district_id", String(form.district_id).replace("id:", ""));
      }
      if (!editingId) {
        fd.append("password", form.password);
      }
      if (form.photo) fd.append("photo", form.photo);

      if (editingId) {
        await API.updateAdminUser(editingId, fd);
        successAlert("User updated", "User updated successfully.");
      } else {
        await API.createAdminUser(fd);
        successAlert("User created", "User created successfully.");
      }
      setForm(initialForm);
      setEditingId(null);
      await fetchUsers();
    } catch (err) {
      const resErrors = err?.errors ? Object.values(err.errors).flat().join(", ") : null;
      errorAlert("Save failed", resErrors || err?.message || "Failed to save user.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "district",
      district: user.district || "",
      district_id: user.district_id
        ? `id:${user.district_id}`
        : (user.district ? `name:${user.district}` : ""),
      photo: null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleResetPassword = async (user) => {
    const result = await Swal.fire({
      title: `Reset password for ${user.name}?`,
      input: "password",
      inputLabel: "Enter new password",
      inputPlaceholder: "New password (min 6 chars)",
      inputAttributes: { autocapitalize: "off", autocorrect: "off" },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value || value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    try {
      await API.resetAdminUserPassword(user.id, result.value);
      successAlert("Password reset", "User password updated successfully");
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Failed to reset password");
    }
  };

  const handleDistrictBlockToggle = async (user) => {
    const userRole = String(user?.role || "").toLowerCase();
    if (userRole !== "district") {
      errorAlert("Not allowed", "Only district logins can be blocked from this panel.");
      return;
    }

    const currentlyBlocked = toBool(user?.is_blocked);
    const shouldBlock = !currentlyBlocked;

    let reason = "";
    if (shouldBlock) {
      const blockResult = await Swal.fire({
        title: `Block ${user.name}?`,
        text: "This district login will be unable to sign in.",
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "Enter reason for blocking",
        showCancelButton: true,
        confirmButtonText: "Block",
        confirmButtonColor: "#dc2626",
      });

      if (!blockResult.isConfirmed) return;
      reason = String(blockResult.value || "").trim();
    } else {
      const { isConfirmed } = await confirmAlert({
        title: `Unblock ${user.name}?`,
        text: "This district login will be able to sign in again.",
        confirmText: "Unblock",
      });
      if (!isConfirmed) return;
    }

    const busyKey = `block-${user.id}`;
    try {
      setRowBusy((prev) => ({ ...prev, [busyKey]: true }));
      await API.updateDistrictUserBlockStatus(user.id, shouldBlock, reason);
      await fetchUsers();
      successAlert(
        shouldBlock ? "District blocked" : "District unblocked",
        shouldBlock ? "District login has been blocked successfully." : "District login has been unblocked successfully."
      );
    } catch (err) {
      errorAlert("Action failed", err?.message || "Failed to update district login status.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleDistrictDelete = async (user) => {
    const userRole = String(user?.role || "").toLowerCase();
    if (userRole !== "district") {
      errorAlert("Not allowed", "Only district logins can be deleted from this panel.");
      return;
    }

    const { isConfirmed } = await confirmAlert({
      title: "Delete district login?",
      text: `${user.name} (${user.email}) will be permanently removed.`,
      confirmText: "Delete",
    });
    if (!isConfirmed) return;

    const busyKey = `delete-${user.id}`;
    try {
      setRowBusy((prev) => ({ ...prev, [busyKey]: true }));
      await API.deleteDistrictUser(user.id);
      await fetchUsers();
      successAlert("District deleted", "District login deleted successfully.");
    } catch (err) {
      errorAlert("Delete failed", err?.message || "Failed to delete district login.");
    } finally {
      setRowBusy((prev) => ({ ...prev, [busyKey]: false }));
    }
  };

  const handleDistrictLogoFileChange = (districtId, file) => {
    setDistrictLogoFiles((prev) => ({ ...prev, [districtId]: file || null }));
  };

  const handleUploadDistrictLogo = async (district) => {
    const districtId = Number(district?.id || 0);
    if (!districtId) {
      errorAlert("Invalid district", "District id is missing");
      return;
    }

    const selectedFile = districtLogoFiles[districtId];
    if (!selectedFile) {
      errorAlert("No file selected", `Please choose a logo for ${district.name}`);
      return;
    }

    try {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: true }));
      const fd = new FormData();
      fd.append("logo", selectedFile);
      await API.updateDistrictLogo(districtId, fd);
      await fetchDistrictOptions();
      setDistrictLogoFiles((prev) => ({ ...prev, [districtId]: null }));
      successAlert("Logo updated", `${district.name} logo saved successfully`);
    } catch (err) {
      errorAlert("Upload failed", err?.message || "Failed to upload district logo");
    } finally {
      setDistrictLogoUploading((prev) => ({ ...prev, [districtId]: false }));
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Access Control</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Login Management</h1>
              <p className="text-sm text-slate-600 mt-1">
                Create district/secretary/admin users, block/delete district logins, and maintain logo assets.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Users</p>
                <p className="text-lg font-semibold text-slate-900">{users.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Districts</p>
                <p className="text-lg font-semibold text-slate-900">{districtOptions.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Editing</p>
                <p className="text-lg font-semibold text-emerald-800">{editingId ? "Yes" : "No"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Saving</p>
                <p className="text-lg font-semibold text-cyan-800">{loading ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow p-4 sm:p-6 mb-8 grid md:grid-cols-2 gap-4">
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            className="border p-2 rounded"
            required
          />
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className="border p-2 rounded"
            required
          />
          {!editingId && (
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Password"
              className="border p-2 rounded"
              required
            />
          )}
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            className="border p-2 rounded"
          >
            <option value="district">District</option>
            <option value="secretary">Secretary</option>
            <option value="admin">Admin</option>
          </select>
          <select
            name="district_id"
            value={form.district_id}
            onChange={handleChange}
            className="border p-2 rounded"
            required={form.role === "district"}
            disabled={form.role !== "district"}
          >
            <option value="">{form.role === "district" ? "Select District" : "Not required for this role"}</option>
            {districtOptions.map((d) => (
              <option key={`${d.id ?? "na"}-${d.name}`} value={d.id ? `id:${d.id}` : `name:${d.name}`}>
                {d.name}
              </option>
            ))}
          </select>
          <input
            name="photo"
            type="file"
            accept="image/*"
            onChange={handleChange}
            className="border p-2 rounded"
          />

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? "Saving..." : editingId ? "Update User" : "Create User"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(initialForm);
                }}
                className="ml-3 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 shadow p-6">
          <h2 className="text-base sm:text-lg font-semibold mb-4">All Login Accounts (Users Table)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-xs sm:text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Photo</th>
                  <th className="p-2 border">Name</th>
                  <th className="p-2 border">Email</th>
                  <th className="p-2 border">District</th>
                  <th className="p-2 border">Role</th>
                  <th className="p-2 border">Login Status</th>
                  <th className="p-2 border">Created At</th>
                  <th className="p-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length ? (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td className="p-2 border text-center">
                        {String(u.photo || "").trim() ? (
                          <img
                            src={resolveAssetUrl(String(u.photo || "").trim())}
                            alt={u.name}
                            className="w-10 h-10 rounded-full object-cover mx-auto"
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-2 border">{u.name}</td>
                      <td className="p-2 border">{u.email}</td>
                      <td className="p-2 border">{u.district || "-"}</td>
                      <td className="p-2 border capitalize">{u.role || "district"}</td>
                      <td className="p-2 border">
                        {String(u.role || "").toLowerCase() === "district" ? (
                          <span className={toBool(u.is_blocked) ? "text-red-700 font-semibold" : "text-emerald-700 font-semibold"}>
                            {toBool(u.is_blocked) ? "Blocked" : "Active"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-2 border">
                        {u.created_at ? new Date(u.created_at).toLocaleString() : "-"}
                      </td>
                      <td className="p-2 border">
                        <div className="flex flex-wrap justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(u)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
                            disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResetPassword(u)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                            disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                          >
                            Reset Password
                          </button>
                          {String(u.role || "").toLowerCase() === "district" && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDistrictBlockToggle(u)}
                                className={`text-white px-3 py-1 rounded ${toBool(u.is_blocked) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} disabled:bg-gray-400`}
                                disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                              >
                                {rowBusy[`block-${u.id}`] ? "Saving..." : (toBool(u.is_blocked) ? "Unblock" : "Block")}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDistrictDelete(u)}
                                className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-1 rounded disabled:bg-gray-400"
                                disabled={!!rowBusy[`block-${u.id}`] || !!rowBusy[`delete-${u.id}`]}
                              >
                                {rowBusy[`delete-${u.id}`] ? "Deleting..." : "Delete"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-3 border text-center text-gray-500" colSpan={8}>
                      No user logins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow p-6 mt-8">
          <h2 className="text-base sm:text-lg font-semibold mb-4">District Logos (For Certificates)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border text-xs sm:text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">District</th>
                  <th className="p-2 border">Current Logo</th>
                  <th className="p-2 border">New Logo</th>
                  <th className="p-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {districtOptions.length ? (
                  districtOptions.map((d) => (
                    <tr key={`district-logo-${d.id || d.name}`}>
                      <td className="p-2 border">{d.name}</td>
                      <td className="p-2 border text-center">
                        {d.logo_path ? (
                          <img
                            src={resolveAssetUrl(d.logo_path, { folder: "districts" })}
                            alt={`${d.name} logo`}
                            className="h-12 object-contain mx-auto"
                          />
                        ) : (
                          <span className="text-gray-500">No logo</span>
                        )}
                      </td>
                      <td className="p-2 border">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleDistrictLogoFileChange(d.id, e.target.files?.[0] || null)}
                          className="border p-1 rounded w-full"
                          disabled={!d.id}
                        />
                      </td>
                      <td className="p-2 border text-center">
                        <button
                          type="button"
                          onClick={() => handleUploadDistrictLogo(d)}
                          disabled={!d.id || !districtLogoFiles[d.id] || !!districtLogoUploading[d.id]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded disabled:bg-gray-400"
                        >
                          {districtLogoUploading[d.id] ? "Uploading..." : "Upload"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-3 border text-center text-gray-500" colSpan={4}>
                      No districts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default DistrictRegistration;
